/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — pure filter model.
 *
 * The Vitals page lets a caregiver narrow the triage table on three orthogonal
 * axes: a status chip (a clinical severity, the "offline" connectivity state,
 * or "stale" readings), a living-environment chip (group home / independent),
 * and a free-text name search. This module holds that UI-free predicate logic
 * so the page component stays presentational.
 *
 * Fail-safe like the rest of the facelift: a malformed row never throws, an
 * unknown status or livingEnv key falls open (keeps everything on that axis)
 * rather than blanking the table, and filtering is non-mutating. Severity
 * vocabulary + staleness come from the shared design tokens / vitalsModel so
 * this stays in lockstep.
 */
import { isStale } from "./vitalsModel";
import { SEVERITY_LEVELS, severity } from "../../styles/designTokens";
import { OFFLINE_STATUS } from "./overviewModel";

// "stale" is a freshness state (derived from lastUpdate), not a row.status, so
// it is its own filter key alongside the severities + offline.
const STALE_FILTER = "stale";
export const ALL_FILTER = "all";

// Chips: All, then each clinical severity, then offline (connectivity) and
// stale (freshness). Built from SEVERITY_LEVELS so a new severity appears here
// automatically. `nora` is informational, not a triage bucket, so it is omitted.
export const VITALS_STATUS_FILTERS = Object.freeze([
  { key: ALL_FILTER, label: "All" },
  ...SEVERITY_LEVELS.filter((lvl) => lvl !== "nora").map((lvl) => ({
    key: lvl,
    label: severity[lvl].label,
  })),
  { key: OFFLINE_STATUS, label: "Offline" },
  { key: STALE_FILTER, label: "Stale" },
]);

const KNOWN_STATUS = new Set(VITALS_STATUS_FILTERS.map((f) => f.key));

// Living-environment chips (a second, orthogonal axis to status — a group-home
// client can also be critical). Reuses the roster's `livingEnv` vocabulary so
// the Vitals page and the Overview roster read the same. `all` keeps every env.
export const VITALS_ENV_FILTERS = Object.freeze([
  { key: ALL_FILTER, label: "All" },
  { key: "group_home", label: "Group Home" },
  { key: "independent", label: "Independent" },
]);

const KNOWN_ENV = new Set(VITALS_ENV_FILTERS.map((f) => f.key));

function matchesStatus(row, status, now, staleMinutes) {
  if (status === ALL_FILTER) return true;
  if (status === STALE_FILTER) return isStale(row.lastUpdate, now, staleMinutes);
  if (status === OFFLINE_STATUS) return row.status === OFFLINE_STATUS;
  // Clinical severity (or any other explicit status): exact match.
  return row.status === status;
}

function matchesEnv(row, livingEnv) {
  if (livingEnv === ALL_FILTER) return true;
  return row.livingEnv === livingEnv;
}

function matchesQuery(row, query) {
  if (!query) return true;
  const name = typeof row.clientName === "string" ? row.clientName.toLowerCase() : "";
  return name.includes(query);
}

/**
 * Filter triage rows by a status chip, a living-environment chip, and a name
 * search (the three axes AND together). Pure, non-mutating, and fail-safe:
 * non-array input -> []; a non-object row is dropped rather than throwing; an
 * unknown status or livingEnv key falls open (keeps everything on that axis) so
 * the table is never silently blanked.
 *
 * @param {Array} rows triage rows: { clientName, status, livingEnv, lastUpdate, ... }
 * @param {object} [opts]
 * @param {string} [opts.status="all"] one of VITALS_STATUS_FILTERS keys
 * @param {string} [opts.livingEnv="all"] one of VITALS_ENV_FILTERS keys (orthogonal to status)
 * @param {string} [opts.query=""] free-text name search (case-insensitive)
 * @param {number} [opts.now=Date.now()] reference time for staleness
 * @param {number} [opts.staleMinutes=15] staleness threshold in minutes
 * @returns {Array} a new, filtered array
 */
export function filterVitalsRows(rows, opts = {}) {
  if (!Array.isArray(rows)) return [];
  const rawStatus = opts.status;
  // Unknown filter -> fall open to "all" so a typo/stale key can't blank the UI.
  const status = KNOWN_STATUS.has(rawStatus) ? rawStatus : ALL_FILTER;
  // livingEnv is optional + backward-compatible: absent/unknown -> "all" (keeps
  // every environment), so existing callers that pass only `status` are unchanged.
  const livingEnv = KNOWN_ENV.has(opts.livingEnv) ? opts.livingEnv : ALL_FILTER;
  const query = typeof opts.query === "string" ? opts.query.trim().toLowerCase() : "";
  const now = typeof opts.now === "number" && Number.isFinite(opts.now) ? opts.now : Date.now();
  const staleMinutes =
    typeof opts.staleMinutes === "number" && opts.staleMinutes >= 0 ? opts.staleMinutes : 15;

  return rows.filter(
    (row) =>
      row !== null &&
      typeof row === "object" &&
      !Array.isArray(row) &&
      matchesStatus(row, status, now, staleMinutes) &&
      matchesEnv(row, livingEnv) &&
      matchesQuery(row, query),
  );
}
